import { defineStore } from 'pinia';
import { ref } from 'vue';

// Deliberately SHORTER than the backend's ceiling. GenerateStickersPdf has
// retryUntil() = now+45min, and an execution starting just under that can run
// another 840s timeout on top — ~59 minutes absolute worst case. Matching that
// would be wrong: a hard OOM/Lambda kill bypasses the job's failed() hook, so
// there is no terminal signal at all in that case, and an hour-long phantom
// skeleton reads as a broken page. 20 minutes still covers a full worst-case
// single execution plus queue wait, and if a run does land after expiry the
// user still gets the file row, the "documents generated" toast and the
// notification — only the skeleton is gone.
const STALE_TIMEOUT_MS = 20 * 60 * 1000;

// localStorage key holding every tracked run so skeletons survive a full page
// refresh. Removed when nothing is tracked.
const STORAGE_KEY = 'stickerGeneration:runs';
const STORAGE_VERSION = 1;

// Media collection the finished PDF lands in (OrderDocumentsCollection::STICKERS).
const STICKERS_COLLECTION = 'STICKERS';

interface StickerRun {
    count: number;
    // Informational only — never compared against server timestamps.
    startedAt: number;
    expiresAt: number;
    // Id of the STICKERS file already on the order when the run started, or
    // null when the order had none. The collection is singleFile(), so a
    // completed run necessarily replaces that row with a new id.
    baselineFileId: number | null;
}

interface StickerFile {
    id?: number;
    collection_name?: string;
    custom_properties?: { sticker_count?: number | string | null } | null;
}

const keyOf = (orderId: number | string): string => String(orderId);

/**
 * Tracks which orders have a sticker PDF run in flight so the uploads list can
 * show a skeleton placeholder. Written by GenerateDocuments.vue when a run is
 * successfully queued; read by OrderUploads.vue.
 *
 * The store is the source of truth for the skeleton: once a run is tracked it
 * outlives the component, so the placeholder is still there when the user
 * navigates away mid-run and comes back, and survives a full page refresh via
 * localStorage. Sticker runs are the slowest document job in the CRM (up to
 * 840s for 2000 stickers), which is why they need this and the other document
 * types don't.
 *
 * One run per order — this mirrors the backend, where
 * WithoutOverlapping("{tenantKey}:{orderId}") serialises runs per order and the
 * STICKERS media collection is singleFile(), so an order has exactly one
 * visible outcome. A second Generate click simply replaces the tracked run.
 *
 * Three independent signals settle a run, because no single one is reliable:
 *  - settle() from the user-channel notification hook in the notifications
 *    store — the only terminal signal that reaches the user on any page;
 *  - reconcile() against the fetched file list — covers a run that finished
 *    while the tab was closed, where no live signal was ever delivered;
 *  - the STALE_TIMEOUT_MS backstop — last resort for a hard worker kill, which
 *    produces no event, no notification and no file.
 */
export const useStickerGenerationStore = defineStore('stickerGeneration', () => {
    const runs = ref(new Map<string, StickerRun>());

    let backstopTimer: ReturnType<typeof setTimeout> | null = null;

    function persist(): void {
        try {
            const payload: Record<string, StickerRun> = {};

            runs.value.forEach((run, key) => {
                payload[key] = run;
            });

            if (Object.keys(payload).length === 0) {
                localStorage.removeItem(STORAGE_KEY);
            } else {
                localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: STORAGE_VERSION, runs: payload }));
            }
        } catch {
            // Ignore storage failures (private mode / quota) — in-memory tracking still works.
        }
    }

    function clearBackstop(): void {
        if (backstopTimer !== null) {
            clearTimeout(backstopTimer);
            backstopTimer = null;
        }
    }

    /**
     * Self-heal when every terminal signal for a run is lost. One timer fires at
     * the soonest expiry across all tracked runs, drops only what has actually
     * expired, then re-arms for the rest.
     */
    function armBackstop(): void {
        clearBackstop();

        let fireAt = Number.POSITIVE_INFINITY;

        runs.value.forEach((run) => {
            fireAt = Math.min(fireAt, run.expiresAt);
        });

        if (!Number.isFinite(fireAt)) {
            return;
        }

        backstopTimer = setTimeout(
            () => {
                backstopTimer = null;

                const now = Date.now();
                let changed = false;

                runs.value.forEach((run, key) => {
                    if (run.expiresAt <= now) {
                        runs.value.delete(key);
                        changed = true;
                    }
                });

                if (changed) {
                    persist();
                }

                armBackstop();
            },
            Math.max(0, fireAt - Date.now()),
        );
    }

    /**
     * Whether a sticker run is in flight for this order.
     *
     * Kept side-effect free — this runs inside a computed on the render path.
     * The expiry is re-checked on read rather than trusted to the backstop so a
     * throttled or suspended tab can never render a stale skeleton.
     */
    function isGenerating(orderId: number | string | null | undefined): boolean {
        if (orderId == null) {
            return false;
        }

        const run = runs.value.get(keyOf(orderId));

        return !!run && run.expiresAt > Date.now();
    }

    /** Sticker count of the in-flight run, for the skeleton's label. */
    function countFor(orderId: number | string | null | undefined): number | null {
        if (orderId == null) {
            return null;
        }

        const run = runs.value.get(keyOf(orderId));

        return run && run.expiresAt > Date.now() ? run.count : null;
    }

    /**
     * Start tracking a queued run. Call this only once the dispatch request has
     * actually succeeded — tracking a request that never queued would leave a
     * skeleton up for the whole STALE_TIMEOUT_MS with no job behind it.
     */
    function start(orderId: number | string, count: number, baselineFileId: number | null = null): void {
        const now = Date.now();

        runs.value.set(keyOf(orderId), {
            count: Number(count),
            startedAt: now,
            expiresAt: now + STALE_TIMEOUT_MS,
            baselineFileId: baselineFileId ?? null,
        });

        persist();
        armBackstop();
    }

    /**
     * Terminal: stop tracking this order's run. A no-op when the order isn't
     * tracked, or when `count` is supplied and doesn't match the tracked run —
     * that guard stops an earlier run's result from clearing a second, still
     * running one that was queued with a different count.
     */
    function settle(orderId: number | string | null | undefined, count: number | string | null = null): void {
        if (orderId == null) {
            return;
        }

        const key = keyOf(orderId);
        const run = runs.value.get(key);

        if (!run) {
            return;
        }

        if (count != null && Number(count) !== run.count) {
            return;
        }

        runs.value.delete(key);
        persist();
        armBackstop();
    }

    /** Whether this STICKERS media row is the output of the tracked run. */
    function matchesRun(run: StickerRun, file: StickerFile | undefined): boolean {
        if (!file) {
            return false;
        }

        const produced = file.custom_properties?.sticker_count;
        const countMatches = produced != null && Number(produced) === run.count;

        if (run.baselineFileId != null) {
            // singleFile() replaces the row, so a different id means our run
            // landed. The count is not required strictly here — older media may
            // predate the sticker_count custom property, and demanding it would
            // pin the skeleton until the backstop fires.
            return file.id !== run.baselineFileId && (produced == null || countMatches);
        }

        // The order had no sticker PDF when we started, so require the count —
        // otherwise a pre-existing file could clear a run that is still going.
        return countMatches;
    }

    /**
     * Settle against a freshly fetched file list. This is the only signal that
     * survives the tab being closed mid-run: the notification reaches a live
     * session only, so the file list is the authoritative "did it land?" check.
     */
    function reconcile(orderId: number | string | null | undefined, files: StickerFile[] | null | undefined): void {
        if (orderId == null) {
            return;
        }

        const run = runs.value.get(keyOf(orderId));

        if (!run) {
            return;
        }

        const stickerFile = (files ?? []).find((file) => file?.collection_name === STICKERS_COLLECTION);

        if (matchesRun(run, stickerFile)) {
            settle(orderId);
        }
    }

    /**
     * Restore tracked runs from localStorage after a full page refresh. Runs
     * once when the store is first created. Drops anything already past its
     * hard expiry so a dead run can't resurrect a zombie skeleton, then re-arms
     * the backstop for the remaining lifetimes.
     */
    function restore(): void {
        let parsed: { version?: number; runs?: Record<string, StickerRun> } | null = null;

        try {
            const raw = localStorage.getItem(STORAGE_KEY);

            if (raw) {
                parsed = JSON.parse(raw);
            }
        } catch {
            parsed = null;
        }

        const now = Date.now();

        // An unknown version is dropped rather than guessed at — there is no
        // earlier format to migrate from.
        if (parsed?.version === STORAGE_VERSION) {
            Object.entries(parsed.runs ?? {}).forEach(([key, run]) => {
                if (typeof run?.expiresAt !== 'number' || run.expiresAt <= now || !Number(run?.count)) {
                    return;
                }

                runs.value.set(key, {
                    count: Number(run.count),
                    startedAt: typeof run.startedAt === 'number' ? run.startedAt : now,
                    expiresAt: run.expiresAt,
                    baselineFileId: typeof run.baselineFileId === 'number' ? run.baselineFileId : null,
                });
            });
        }

        // Rewrites the key from whatever actually survived, and removes it
        // outright when nothing did — which is also how a corrupt or
        // unknown-version payload gets cleaned up.
        persist();
        armBackstop();
    }

    // Resume runs persisted before a full page refresh.
    restore();

    return {
        runs,
        isGenerating,
        countFor,
        start,
        settle,
        reconcile,
        restore,
    };
});
